import {ThumbnailType} from '@Components/thumbnail/thumbnail.types';
import type {ListItemStorage} from '@Components/list/list.types';
import {LIST_ITEM_TYPE} from '@Components/list/list.types';
import type {ListItemType1Storage} from '@Components/list/components/list-item-type-1';
import {onPOSItemsUpdated} from '@Panels/user-menu-panel/user-menu-panel-reducer';
import {updateListsState} from '@Components/list/list-reducer';
import {openErrorModal} from '@Modals/error-modal';
import type {MenuItemData, MenuItemIcon, MenuItemTag, MenuItemVariation, PosCatalogBackendData, PosMenuItem, PosMenuItemVariation, TABS} from './user-menu-panel.types';
import {ImageSrc, MenuItemType, PosLoadingStates, POS_MENU_LIST_ID, USER_MENU_MODAL_TABS_ID} from './user-menu-panel.types';

export const cloneMenuItem = (item: MenuItemData): MenuItemData => {
  const menuItem = {
    id: item.id,
    name: item.name,
    price: item.price,
    description: item.description,
    type: item.type,
    variationsList: item.variationsList.map((variation: MenuItemVariation) => {
      return cloneitemVariation(variation);
    }),
    addOnsList: item.addOnsList.map((variation: MenuItemVariation) => {
      return cloneitemVariation(variation);
    }),
    iconsList: [...item.iconsList],
    categories: [...item.categories],
  } as MenuItemData;
  if (menuItem.type === MenuItemType.POS_MENU_ITEM && item.type === MenuItemType.POS_MENU_ITEM) {
    menuItem.source = item.source;
    menuItem.posItemId = item.posItemId;
  }
  return menuItem;
};

export const menuItemToListStorage = (item: MenuItemData, onEditItem?: Function, onSelectItem?: Function, isSelected?: boolean, isLoading?: boolean): ListItemType1Storage => {
  const menuItem = {
    id: item.id,
    text: item.name,
    subtext: item.price,
    loading: !!isLoading,
    showEditIcon: !!onEditItem,
    additionalData: {
      description: item.description,
      variationsList: item.variationsList.map((variation: MenuItemVariation) => {
        return cloneitemVariation(variation);
      }),
      addOnsList: item.addOnsList.map((variation: MenuItemVariation) => {
        return cloneitemVariation(variation);
      }),
      categories: item.categories,
      iconsList: [...item.iconsList],
    },
    isSelected,
    type: LIST_ITEM_TYPE.DEFAULT_1,
    onClickEditIcon: onEditItem
      ? () => {
          return onEditItem(item);
        }
      : null,
    onClick: onSelectItem
      ? () => {
          return onSelectItem(item);
        }
      : null,
  } as ListItemType1Storage;
  if (item.type === MenuItemType.POS_MENU_ITEM) {
    menuItem.thumbnail = {
      type: ThumbnailType.IMAGE,
      imageSrc: window.PMW.util.asset_url(ImageSrc.SQUARE),
    };
    menuItem.additionalData = {
      ...menuItem.additionalData,
      source: item.source,
      posItemId: item.posItemId,
    };
  }
  return menuItem;
};

export const cloneitemVariation = (variation: MenuItemVariation): MenuItemVariation => {
  const menuItemVariation = {
    id: variation.id,
    name: variation.name,
    price: variation.price,
    type: variation.type,
  } as MenuItemVariation;
  if (variation.type === MenuItemType.POS_MENU_ITEM && menuItemVariation.type === MenuItemType.POS_MENU_ITEM) {
    menuItemVariation.posVariationId = variation.posVariationId;
  }
  return menuItemVariation;
};

export const initMenuItem = (item?: MenuItemData): MenuItemData => {
  return item
    ? cloneMenuItem(item)
    : ({
        id: '',
        name: '',
        price: '',
        description: '',
        type: MenuItemType.USER_MENU_ITEM,
        iconsList: [],
        variationsList: [],
        addOnsList: [],
        categories: [],
      } as MenuItemData);
};

export const onError = (message?: string): void => {
  openErrorModal({
    message: message ?? window.i18next.t('pmwjs_connectivity_error'),
    log: false
  });
};

export const getPosLoadingState = (): PosLoadingStates => {
  return window.PMW.redux.store.getState().menus.posLoadingState;
};

export const getActiveTab = (): TABS => {
  return window.PMW.redux.store.getState().tabs.selectedTabsHashmap[USER_MENU_MODAL_TABS_ID] as TABS;
};

export const getMenuItemsHash = (items: string[], hashMap: Record<string, ListItemStorage>, type: MenuItemType): Record<string, MenuItemData> => {
  const menuHashMap: Record<string, MenuItemData> = {};
  if (items) {
    items.map((itemId) => {
      const item = hashMap[itemId] as ListItemType1Storage;
      menuHashMap[itemId] = {
        id: item.id,
        name: item.text,
        price: item.subtext,
        description: item.additionalData?.description,
        type,
        iconsList: item.additionalData?.iconsList,
        variationsList: item.additionalData?.variationsList,
        addOnsList: item.additionalData?.addOnsList,
        categories: item.additionalData?.categories,
      } as MenuItemData;
      if (item.additionalData?.source) {
        menuHashMap[itemId] = {
          ...menuHashMap[itemId],
          type: MenuItemType.POS_MENU_ITEM,
          source: item.additionalData?.source,
          posItemId: item.additionalData?.posItemId,
        } as PosMenuItem;
      }
    });
  }
  return menuHashMap;
};

export const getListData = (listId: string, itemIds?: string[], items?: ListItemType1Storage[]) => {
  return {
    listId,
    listItemsIds: itemIds ?? [],
    listItems: items ?? [],
    draggable: true,
    multiSelect: true,
    keepAtleastOneItemSelected: false,
  };
};

export const getSelectedTagIds = (tags: MenuItemTag[]): string[] => {
  const selectedTags: string[] = [];
  tags.forEach((tag) => {
    if (tag.isSelected) {
      selectedTags.push(tag.id);
    }
  });
  return selectedTags;
};

export const getSelectedTagNames = (tags: MenuItemTag[]): string[] => {
  const selectedTags: string[] = [];
  tags.forEach((tag) => {
    if (tag.isSelected) {
      selectedTags.push(tag.name);
    }
  });
  return selectedTags;
};

export const getUpdatedLoadingToken = (): string => {
  return window.PMW.redux.store.getState().menus.loadingToken;
};

const updatePosItems = (items: ListItemType1Storage[], itemIds: string[], loadingToken: string, categories?: MenuItemTag[], cursor?: string, loadMore?: boolean): void => {
  if (loadingToken !== getUpdatedLoadingToken()) {
    return;
  }
  window.PMW.redux.store.dispatch(
    updateListsState([
      {
        listId: POS_MENU_LIST_ID,
        listItemsIds: itemIds,
        listItems: items,
        draggable: false,
        multiSelect: true,
        keepAtleastOneItemSelected: false,
        keepExitingItems: loadMore,
      },
    ])
  );
  window.PMW.redux.store.dispatch(
    onPOSItemsUpdated({
      posCategories: categories,
      posLoadingState: PosLoadingStates.LOADED_ITEMS,
      posListCursor: cursor,
      searchingWithTags: false,
    })
  );
};

export const loadPosMenuItems = async (categoryFilter?: string[], loadMore?: boolean, cursor?: string) => {
  try {
    const {loadingToken} = window.PMW.redux.store.getState().menus;
    const response = (await window.PMW.readLocal('square/getMenuItems', {
      categories: categoryFilter,
      cursor: cursor,
    })) as PosCatalogBackendData;
    const {items, categories} = response;
    const itemIds: string[] = [];
    const menuItems: ListItemType1Storage[] = [];
    items.forEach((item: PosMenuItem): void => {
      const menuItem: PosMenuItem = {
        posItemId: item.posItemId,
        type: MenuItemType.POS_MENU_ITEM,
        id: item.id,
        name: item.name,
        price: item.price,
        description: item.description,
        categories: item.categories,
        iconsList: [],
        variationsList: [],
        addOnsList: [],
        source: item.source,
      };
      for (const posItemVariation of item.variationsList) {
        menuItem.variationsList.push({
          name: posItemVariation.name,
          price: posItemVariation.price,
          posVariationId: posItemVariation.id,
          type: MenuItemType.POS_MENU_ITEM,
        } as PosMenuItemVariation);
      }
      itemIds.push(menuItem.id);
      menuItems.push(menuItemToListStorage(menuItem));
    });
    updatePosItems(menuItems, itemIds, loadingToken, categories, response.cursor, loadMore);
  } catch (e) {
    onError();
  }
};

export const getMenuItemIcons = (): MenuItemIcon[] => {
  return [
    {id: "1", name: "Leaf"},
    {id: "2", name: "Flower"},
    {id: "3", name: "Chili"},
    {id: "4", name: "Chilies"},
    {id: "5", name: "Chilies"},
    {id: "6", name: "Chili"},
    {id: "7", name: "Chilies"},
    {id: "8", name: "Chilies"},
    {id: "9", name: "Spicy"},
    {id: "10", name: "Spicy"},
    {id: "86", name: "Nuts"},
    {id: "87", name: "Nuts"},
    {id: "11", name: "Chef"},
    {id: "12", name: "Chef"},
    {id: "13", name: "King"},
    {id: "14", name: "Parallel Lines"},
    {id: "15", name: "Fat Free"},
    {id: "16", name: "Low Fat"},
    {id: "17", name: "Low Fat"},
    {id: "18", name: "Low Fat"},
    {id: "19", name: "New"},
    {id: "20", name: "New"},
    {id: "21", name: "New"},
    {id: "22", name: "New"},
    {id: "23", name: "New"},
    {id: "24", name: "New"},
    {id: "25", name: "New"},
    {id: "26", name: "New"},
    {id: "27", name: "For kids"},
    {id: "28", name: "Kids"},
    {id: "29", name: "Low Carbs"},
    {id: "30", name: "Hot"},
    {id: "31", name: "Hot"},
    {id: "32", name: "Local"},
    {id: "33", name: "Local"},
    {id: "34", name: "Special"},
    {id: "35", name: "Spicy"},
    {id: "36", name: "Spicy"},
    {id: "37", name: "Vegan"},
    {id: "38", name: "Vegan"},
    {id: "39", name: "Veggie"},
    {id: "40", name: "Made Here"},
    {id: "41", name: "Made Here"},
    {id: "42", name: "Low Calories"},
    {id: "43", name: "Low Calories"},
    {id: "44", name: "Low Calories"},
    {id: "45", name: "Low Calories"},
    {id: "46", name: "Low Calories"},
    {id: "47", name: "Dairy free"},
    {id: "48", name: "Gluten Free"},
    {id: "49", name: "Gluten Free"},
    {id: "50", name: "Point Right"},
    {id: "51", name: "Point Left"},
    {id: "52", name: "Point Right"},
    {id: "53", name: "Point Left"},
    {id: "54", name: "Point Right"},
    {id: "55", name: "Point Left"},
    {id: "56", name: "Tick"},
    {id: "57", name: "Cross"},
    {id: "58", name: "Star"},
    {id: "59", name: "Star"},
    {id: "60", name: "Circle"},
    {id: "61", name: "Circle"},
    {id: "62", name: "Arrow Right"},
    {id: "63", name: "Arrow Left"},
    {id: "64", name: "Arrow Right"},
    {id: "65", name: "Arrow Left"},
    {id: "66", name: "Arrow Right"},
    {id: "67", name: "Arrow Left"},
    {id: "68", name: "Arrow Right"},
    {id: "69", name: "Arrow Left"},
    {id: "70", name: "Arrow Right"},
    {id: "71", name: "Arrow Left"},
    {id: "72", name: "Arrow Right"},
    {id: "73", name: "Arrow Left"},
    {id: "74", name: "Arrow Right"},
    {id: "75", name: "Arrow Left"},
    {id: "76", name: "One"},
    {id: "77", name: "Two"},
    {id: "78", name: "Three"},
    {id: "79", name: "Four"},
    {id: "80", name: "Five"},
    {id: "81", name: "Six"},
    {id: "82", name: "Seven"},
    {id: "83", name: "Eight"},
    {id: "84", name: "Nine"},
    {id: "85", name: "Ten"},
    {id: "88", name: "Halal"},
    {id: "89", name: "Halal"},
    {id: "90", name: "Halal"},
    {id: "91", name: "Halal"},
    {id: "92", name: "Keto"},
    {id: "93", name: "Keto"},
    {id: "94", name: "Kosher"},
    {id: "95", name: "Kosher"},
    {id: "96", name: "Peanuts"},
    {id: "97", name: "Peanuts"},
    {id: "98", name: "Peanuts"},
    {id: "99", name: "Popular"},
    {id: "100", name: "Popular"},
    {id: "101", name: "Popular"},
    {id: "102", name: "Popular"},
    {id: "103", name: "Shell Fish"},
    {id: "104", name: "Shell Fish"},
    {id: "105", name: "Sugar Free"},
    {id: "106", name: "Sugar Free"},
    {id: "107", name: "Sugar Free"},
    {id: "108", name: "V"},
    {id: "109", name: "V"},
    {id: "110", name: "V"},
    {id: "111", name: "VG"},
    {id: "112", name: "VG"},
    {id: "113", name: "VG"},
    {id: "114", name: "LG"},
  ]
}
